<template>
  <z-popper ref="popperRef" :role="role">
    <z-tooltip-trigger
      :disabled="disabled"
      :trigger="trigger"
      :trigger-keys="triggerKeys"
      :virtual-ref="resolvedVirtualRef"
      :virtual-triggering="resolvedVirtualTriggering"
      :focus-on-target="focusOnTarget"
    >
      <slot v-if="$slots.default" />
    </z-tooltip-trigger>
    <z-tooltip-content
      ref="contentRef"
      :aria-label="ariaLabel"
      :boundaries-padding="boundariesPadding"
      :content="resolvedContent"
      :disabled="disabled"
      :effect="effect"
      :enterable="enterable"
      :fallback-placements="fallbackPlacements"
      :hide-after="hideAfter"
      :gpu-acceleration="gpuAcceleration"
      :offset="offset"
      :persistent="persistent"
      :popper-class="kls"
      :popper-style="popperStyle"
      :placement="placement"
      :popper-options="popperOptions"
      :arrow-offset="arrowOffset"
      :pure="pure"
      :raw-content="rawContent"
      :reference-el="referenceEl"
      :trigger-target-el="triggerTargetEl"
      :show-after="showAfter"
      :strategy="strategy"
      :teleported="teleported"
      :transition="transition"
      :virtual-triggering="resolvedVirtualTriggering"
      :z-index="zIndex"
      :append-to="appendTo"
      :loop="loop"
    >
      <slot name="content">
        <span v-if="rawContent" v-html="resolvedContent" />
        <span v-else>{{ resolvedContent }}</span>
      </slot>
      <z-popper-arrow v-if="showArrow" />
    </z-tooltip-content>
  </z-popper>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onDeactivated,
  provide,
  readonly,
  ref,
  toRef,
  unref,
  useSlots,
  watch,
} from 'vue'
import {
  ZPopper,
  ZPopperArrow,
} from '@zoryn/components/popper'
import { isBoolean } from '@zoryn/utils'
import {
  useDelayedToggle,
  useId,
  useNamespace,
  usePopperContainer,
} from '@zoryn/hooks'
import { TOOLTIP_INJECTION_KEY } from './constants'
import { tooltipEmits, useTooltipModelToggle, useTooltipProps } from './tooltip'
import ZTooltipTrigger from './trigger.vue'
import ZTooltipContent from './content.vue'

import type { Mutable } from '@zoryn/utils'
import type { TooltipContentInstance } from './content'
import type { Measurable } from '@zoryn/components/popper'
import type { PopperInstance } from '@zoryn/components/popper'

defineOptions({
  name: 'ZTooltip',
})

const props = defineProps(useTooltipProps)
const emit = defineEmits(tooltipEmits as Mutable<typeof tooltipEmits>)
const slots = useSlots()

usePopperContainer()

const ns = useNamespace('tooltip')
const id = useId()
const popperRef = ref<PopperInstance>()
const contentRef = ref<TooltipContentInstance>()

const updatePopper = () => {
  const popperComponent = unref(popperRef)
  if (popperComponent) {
    popperComponent.popperInstanceRef?.update()
  }
}
const open = ref(false)
const toggleReason = ref<Event>()
/** 表格等场景：命令式 open(target, content) */
const imperativeVirtualRef = ref<Measurable>()
const imperativeContent = ref('')
const imperativeMode = ref(false)
const resolvedVirtualRef = computed(() =>
  imperativeMode.value ? imperativeVirtualRef.value : props.virtualRef
)
const resolvedVirtualTriggering = computed(
  () =>
    imperativeMode.value ||
    !!props.virtualTriggering ||
    !slots.default
)
const resolvedContent = computed(
  () => imperativeContent.value || props.content || ''
)

const { show, hide, hasUpdateHandler } = useTooltipModelToggle({
  indicator: open,
  toggleReason,
})

const { onOpen, onClose } = useDelayedToggle({
  showAfter: toRef(props, 'showAfter'),
  hideAfter: toRef(props, 'hideAfter'),
  autoClose: toRef(props, 'autoClose'),
  open: show,
  close: hide,
})

const controlled = computed(
  () => isBoolean(props.visible) && !hasUpdateHandler.value
)

const kls = computed(() => {
  return [ns.b(), props.popperClass!]
})

provide(TOOLTIP_INJECTION_KEY, {
  controlled,
  id,
  open: readonly(open),
  trigger: toRef(props, 'trigger'),
  onOpen,
  onClose,
  onToggle: (event?: Event) => {
    if (unref(open)) {
      onClose(event)
    } else {
      onOpen(event)
    }
  },
  onShow: () => {
    emit('show', toggleReason.value)
  },
  onHide: () => {
    emit('hide', toggleReason.value)
  },
  onBeforeShow: () => {
    emit('before-show', toggleReason.value)
  },
  onBeforeHide: () => {
    emit('before-hide', toggleReason.value)
  },
  updatePopper,
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled && open.value) {
      open.value = false
    }
    if (!disabled && isBoolean(props.visible)) {
      open.value = props.visible
    }
  }
)

const isFocusInsideContent = (event?: FocusEvent) => {
  return contentRef.value?.isFocusInsideContent(event)
}

onDeactivated(() => open.value && hide())

onBeforeUnmount(() => {
  toggleReason.value = undefined
})

/**
 * 命令式打开（z-table 单元格溢出提示）
 */
const openTooltip = (target: HTMLElement, content: string) => {
  if (!target) {
    return nextTick()
  }
  imperativeVirtualRef.value = target
  imperativeContent.value = content ?? ''
  imperativeMode.value = true
  onOpen(undefined, 0)
  return nextTick(() => updatePopper())
}

/** 命令式关闭 */
const closeTooltip = () => {
  hide()
  imperativeContent.value = ''
  imperativeMode.value = false
  imperativeVirtualRef.value = undefined
  return nextTick()
}

/** 保持 tooltip 激活态（切换单元格时由表格控制） */
const setActived = (_val: boolean) => {}

defineExpose({
  /**
   * @description z-popper component instance
   */
  popperRef,
  /**
   * @description z-tooltip-content component instance
   */
  contentRef,
  /**
   * @description validate current focus event is trigger inside z-tooltip-content
   */
  isFocusInsideContent,
  /**
   * @description update z-popper component instance
   */
  updatePopper,
  /**
   * @description expose onOpen function to mange z-tooltip open state
   */
  onOpen,
  /**
   * @description expose onClose function to manage z-tooltip close state
   */
  onClose,
  /**
   * @description expose hide function
   */
  hide,
  /**
   * @description imperative API for table overflow tooltip
   */
  open: openTooltip,
  close: closeTooltip,
  setActived,
})
</script>
